import { flattenDeep } from 'lodash-es';
import { isBlankValue } from '../objects/isBlankValue';
import { isEmptyArray } from '../objects/isEmpty';

export type MergeableList<T> = Parameters<typeof flattenDeep<T | undefined>>[0];
export type MergeInput<T> = T | MergeableList<T> | undefined;

export function getArray<T = unknown>(values: MergeInput<T>): T[] {
	if (isBlankValue(values)) {
		return [];
	}

	// We got an array
	if (Array.isArray(values)) {
		return flattenDeep<T>(values);
	}

	// We got a single value
	const singleValue = values as T;
	return flattenDeep<T>([singleValue]);
}

export function mergeArrays<T = unknown>(...arrays: Array<MergeInput<T>>): T[] {
	const merged = arrays.reduce<T[]>((acc, current) => {
		const additionalValues = getArray(current);

		// Nothing to add, return the accumulator unchanged
		if (isEmptyArray(additionalValues)) {
			return acc;
		}

		// Accumulator was empty, just return the rest
		if (isEmptyArray(acc)) {
			return additionalValues;
		}

		// Combine the lists
		return acc.concat(additionalValues);
	}, []);

	return merged;
}
